import React, { useState, useEffect } from 'react';
import { FaPlug, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';
import { SiZerodha } from 'react-icons/si';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AccountSidebar from '../components/AccountSidebar';
import ZerodhaConnectModal from '../components/ZerodhaConnectModal';
import api from '../api';

const BrokerIntegrationsPage = () => {
  const [connectedBrokers, setConnectedBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showZerodhaModal, setShowZerodhaModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);

  useEffect(() => {
    fetchConnectedBrokers();
  }, []);

  const fetchConnectedBrokers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/brokers/');
      setConnectedBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (brokerId) => {
    if (!window.confirm('Are you sure you want to disconnect this broker? This will remove all saved credentials.')) {
      return;
    }

    try {
      setDisconnecting(brokerId);
      await api.delete(`/api/v1/brokers/${brokerId}`);
      await fetchConnectedBrokers();
    } catch (error) {
      console.error('Failed to disconnect broker:', error);
      alert('Failed to disconnect broker. Please try again.');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleToggleStatus = async (brokerId) => {
    try {
      await api.patch(`/api/v1/brokers/${brokerId}/toggle`);
      await fetchConnectedBrokers();
    } catch (error) {
      console.error('Failed to toggle broker status:', error);
      alert('Failed to update broker status. Please try again.');
    }
  };

  const availableBrokers = [
    {
      id: 'zerodha',
      name: 'Zerodha',
      description: 'Connect your Zerodha Kite account for automated trading',
      icon: <SiZerodha className="text-5xl text-blue-600" />,
      color: 'blue',
      isConnected: connectedBrokers.some(b => b.broker === 'ZERODHA')
    }
  ];

  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: 'url(/assets/profilepage/profile_page_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center py-16 lg:py-12">
        <div className="max-w-5xl w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-4">
              <AccountSidebar activePage="brokers" />
              
              <div className="md:col-span-3">
                <div className="bg-white rounded-r-2xl p-8">
                  {/* Header */}
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Broker Integrations</h1>
                    <p className="text-gray-600">Connect your trading accounts to automate order placement</p>
                  </div>

                  {/* Available Brokers Section */}
                  <div className="mb-10">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <FaPlug className="mr-2 text-blue-600" />
                      Available Brokers
                    </h2>
                    
                    <div className="grid grid-cols-1 gap-4">
                      {availableBrokers.map((broker) => (
                        <div 
                          key={broker.id}
                          className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-4">
                              <div className="flex-shrink-0">
                                {broker.icon}
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  {broker.name}
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                  {broker.description}
                                </p>
                                {broker.isConnected && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <FaCheck className="mr-1" />
                                    Connected
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <button
                              onClick={() => setShowZerodhaModal(true)}
                              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                broker.isConnected
                                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {broker.isConnected ? 'Reconnect' : 'Connect'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Connected Brokers Section */}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      Connected Accounts
                    </h2>
                    
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <FaSpinner className="animate-spin text-3xl text-blue-600" />
                      </div>
                    ) : connectedBrokers.length === 0 ? (
                      <div className="text-center py-12 border border-gray-200 rounded-lg">
                        <FaPlug className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No brokers connected</h3>
                        <p className="text-gray-500">Connect a broker account to start automated trading</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {connectedBrokers.map((broker) => (
                          <div 
                            key={broker.id}
                            className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0">
                                  {broker.broker === 'ZERODHA' && (
                                    <SiZerodha className="text-4xl text-blue-600" />
                                  )}
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {broker.broker}
                                  </h3>
                                  <div className="flex items-center space-x-4 mt-1">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      broker.is_active 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {broker.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      Connected: {new Date(broker.created_at).toLocaleDateString()}
                                    </span>
                                    {broker.last_token_refresh && (
                                      <span className="text-sm text-gray-500">
                                        Last refresh: {new Date(broker.last_token_refresh).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => handleToggleStatus(broker.id)}
                                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                  {broker.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  onClick={() => handleDisconnect(broker.id)}
                                  disabled={disconnecting === broker.id}
                                  className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                  {disconnecting === broker.id ? (
                                    <>
                                      <FaSpinner className="animate-spin mr-2" />
                                      Disconnecting...
                                    </>
                                  ) : (
                                    <>
                                      <FaTimes className="mr-2" />
                                      Disconnect
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">🔒 Security Notice</h4>
                    <p className="text-sm text-blue-800">
                      Your broker credentials are encrypted and stored securely. We never have access to your trading password.
                      You can disconnect your broker account at any time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />

      {/* Zerodha Connect Modal */}
      {showZerodhaModal && (
        <ZerodhaConnectModal 
          isOpen={showZerodhaModal}
          onClose={() => setShowZerodhaModal(false)}
          onSuccess={() => {
            setShowZerodhaModal(false);
            fetchConnectedBrokers();
          }}
        />
      )}
    </div>
  );
};

export default BrokerIntegrationsPage;

